import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchTeamMember } from '../services/team';
import { team as defaultTeam } from '../data/team';
import { Badge } from '../components/ui/badge';
import { Award, ArrowLeft, Calendar, Globe, Mail, Phone } from 'lucide-react';

// Convertit tout lien YouTube (watch, youtu.be, shorts) en URL d'intégration (embed)
const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    if (url.includes('/embed/')) return url;
    let id = '';
    if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1].split(/[?&]/)[0];
    else if (url.includes('watch?v=')) id = new URL(url).searchParams.get('v') || '';
    else if (url.includes('/shorts/')) id = url.split('/shorts/')[1].split(/[?&]/)[0];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  } catch { return url; }
};

const TeamProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [error, setError] = useState('');
  const heroRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await fetchTeamMember(slug);
        const d = (defaultTeam || []).find(x => x.slug === slug);
        setMember({
          ...d,
          ...m,
          joined_year: m.joined_year ?? m.joinedYear ?? d?.joined_year ?? d?.joinedYear,
          image: (m.image && m.image.trim()) ? m.image : (d?.image || ''),
          images: (Array.isArray(m.images) && m.images.length > 0) ? m.images : (Array.isArray(d?.images) ? d.images : []),
          videos: (Array.isArray(m.videos) && m.videos.length > 0) ? m.videos : (Array.isArray(d?.videos) ? d.videos : []),
          bio: (m.bio && m.bio.trim()) ? m.bio : (d?.bio || ''),
          specialties: (Array.isArray(m.specialties) && m.specialties.length > 0) ? m.specialties : (Array.isArray(d?.specialties) ? d.specialties : []),
          achievements: (Array.isArray(m.achievements) && m.achievements.length > 0) ? m.achievements : (Array.isArray(d?.achievements) ? d.achievements : []),
          vision: (Array.isArray(m.vision) && m.vision.length > 0) ? m.vision : (Array.isArray(d?.vision) ? d.vision : []),
          social: m.social || d?.social || {}
        });
      } catch (e) {
        const fallback = defaultTeam.find(t => t.slug === slug);
        if (fallback) {
          setMember({
            ...fallback,
            joined_year: fallback.joined_year ?? fallback.joinedYear ?? fallback.joined_year,
            images: Array.isArray(fallback.images) ? fallback.images : [],
            videos: Array.isArray(fallback.videos) ? fallback.videos : [],
            specialties: Array.isArray(fallback.specialties) ? fallback.specialties : [],
            achievements: Array.isArray(fallback.achievements) ? fallback.achievements : [],
            vision: Array.isArray(fallback.vision) ? fallback.vision : [],
            social: fallback.social || {}
          });
          setError('');
        } else {
          setError('Profil introuvable');
        }
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!member) return;
    const el = heroRef.current;
    const title = titleRef.current;
    if (!el || !title) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const bgTranslate = Math.min(40, y * 0.12);
        const titleTranslate = Math.min(18, y * 0.08);
        el.style.transform = `translateY(${bgTranslate}px)`;
        title.style.transform = `translateY(${titleTranslate}px)`;
        ticking = false;
      });
      ticking = true;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [member]);

  const displayImage = useMemo(() => {
    if (!member) return '';
    const base = member.image || (Array.isArray(member.images) && member.images.length > 0 ? member.images[0] : '');
    const cacheKey = member.updated_at ? new Date(member.updated_at).getTime() : '';
    return cacheKey && base ? `${base}${base.includes('?') ? '&' : '?'}v=${cacheKey}` : base;
  }, [member]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const lightboxImages = useMemo(() => {
    const imgs = Array.isArray(member?.images) ? member.images.filter(Boolean) : [];
    const main = displayImage;
    // Ensure main image is first and unique
    const rest = imgs.filter((u) => u !== main);
    return main ? [main, ...rest] : imgs;
  }, [member, displayImage]);

  const openLightboxAt = (idx = 0) => {
    if (!lightboxImages || lightboxImages.length === 0) return;
    setLightboxIndex(Math.max(0, Math.min(idx, lightboxImages.length - 1)));
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = (e) => { e?.stopPropagation?.(); setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length); };
  const nextImage = (e) => { e?.stopPropagation?.(); setLightboxIndex((i) => (i + 1) % lightboxImages.length); };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxImages.length]);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <p className="mb-6">{error}</p>
        <button onClick={() => navigate('/equipe')} className="underline">Retour à l'équipe</button>
      </div>
    );
  }

  if (!member) {
    return <div className="max-w-6xl mx-auto px-4 py-16 text-center text-muted-foreground">Chargement…</div>;
  }

  return (
    <div className="min-h-screen">
      {/* HERO avec image de fond et overlay */}
      <div className="relative h-[280px] md:h-[360px] overflow-hidden">
        {displayImage && (
          <img
            src={displayImage}
            alt={member.name}
            className="absolute inset-0 w-full h-full object-cover scale-105 opacity-60"
          />
        )}
        <div ref={heroRef} className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background/95" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 h-full flex items-end pb-6">
          <div ref={titleRef} className="will-change-transform w-full">
            <div className="flex items-end gap-4">
              {displayImage && (
                <img src={displayImage} alt={member.name} className="w-20 h-20 md:w-24 md:h-24 rounded-lg border border-border/60 shadow-md object-cover" />
              )}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-1">{member.name}</h1>
                {member.role && <p className="text-primary font-semibold">{member.role}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {member.joined_year && <Badge variant="outline"><Calendar className="w-3 h-3 mr-1"/> Depuis {member.joined_year}</Badge>}
                  {member.location && <Badge variant="outline"><Globe className="w-3 h-3 mr-1"/> {member.location}</Badge>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENU */}
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-10">
        {/* Présentation + photo principale en grille */}
        <section className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3">
            <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Présentation</h2>
              <p className="text-muted-foreground leading-relaxed text-[15.5px] md:text-base">
                {member.bio || "Informations à venir."}
              </p>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm overflow-hidden">
              <button type="button" onClick={() => openLightboxAt(0)} className="aspect-[4/3] w-full bg-muted/20 group">
                {displayImage ? (
                  <img src={displayImage} alt={member.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">Photo à venir</div>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Galerie */}
        {member.images && member.images.length > 0 && (
          <section>
            <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Galerie</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {member.images.map((src, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => openLightboxAt(lightboxImages.findIndex(u => u === src))}
                    className="group block overflow-hidden rounded-lg border border-border/50 bg-black/5"
                  >
                    <img src={src} alt={`${member.name} ${idx+1}`} className="w-full h-40 md:h-36 lg:h-40 object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Séquences vidéo */}
        <section>
          <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Séquences vidéo</h2>
              <span className="text-xs text-muted-foreground">(Ajout depuis l'admin prochainement)</span>
            </div>
            {member.videos && member.videos.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {member.videos.map((url, idx) => (
                  <div key={url || idx} className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 bg-black">
                    {url.endsWith('.mp4') ? (
                      <video src={url} controls className="w-full h-full" />
                    ) : (
                      <iframe
                        src={toEmbedUrl(url)}
                        title={`video-${idx}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Aucune vidéo pour le moment.</div>
            )}
          </div>
        </section>

        {/* Spécialités & Réalisations */}
        <section className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
            <h3 className="font-semibold mb-3">Spécialités</h3>
            {Array.isArray(member.specialties) && member.specialties.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {member.specialties.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">—</div>
            )}
          </div>
          <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
            <h3 className="font-semibold mb-3 flex items-center"><Award className="w-4 h-4 mr-2 text-accent"/> Réalisations marquantes</h3>
            {Array.isArray(member.achievements) && member.achievements.length > 0 ? (
              <ul className="space-y-2">
                {member.achievements.map((a, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start"><span className="text-primary mr-2">•</span>{a}</li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">—</div>
            )}
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="rounded-xl border border-border/60 bg-background/60 shadow-sm p-6">
            <h3 className="font-semibold mb-2">Contact</h3>
            <a href={`mailto:${member.social?.email || 'padaid.asso@gmail.com'}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border/60 hover:bg-primary/10 transition-colors">
              <Mail className="w-4 h-4"/> {member.social?.email || 'padaid.asso@gmail.com'}
            </a>
          </div>
        </section>
      </div>
      {/* Lightbox Overlay */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-sm border border-white/20 rounded px-3 py-1"
          >
            Fermer
          </button>

          <button
            type="button"
            onClick={prevImage}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-2xl"
          >
            ‹
          </button>

          <div className="max-w-[92vw] max-h-[82vh] p-2">
            <img
              src={lightboxImages[lightboxIndex]}
              alt={`media-${lightboxIndex}`}
              className="max-w-full max-h-[80vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <button
            type="button"
            onClick={nextImage}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-2xl"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamProfile;